import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, LabelList } from 'recharts'
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import { Card, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'
import { truncate } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useFetchQuestionRubricsApproveAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import useRubricContext from '../../../../../../../hooks/useRubricContext'

const BAR_HEIGHT = 36

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload
        return (
            <Card size='small'>
                <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                    {data.question}
                </Title>
                <Text>{`${data.percent}% - ${data.number} submissions`}</Text>
            </Card>
        )
    }
    return null
}

const CustomBar = props => {
    const { x, y, width, height, value } = props
    const colors = useThemeColors()

    // Don't render any bar when value is 0
    if (value === 0) {
        return null
    }

    const fill = value === 100 ? colors.success : colors.primary

    return <rect x={x} y={y} width={width} height={Math.min(height, 48)} fill={fill} cursor='pointer' rx={4} ry={4} />
}

const CustomLabel = props => {
    const { x, y, width, height, value } = props
    const colors = useThemeColors()

    // Position label at start of bar area when there's no bar (value is 0)
    const labelX = value === 0 ? x + 8 : x + width + 8

    return (
        <text x={labelX} y={y + height / 2} dy={4} textAnchor='start' fill={colors.secondaryText} fontSize='12'>
            {`${value}%`}
        </text>
    )
}

const HumanReviewProgressChart = ({ token }) => {
    const navigate = useNavigate()

    const { data: analyticsPerQuestionRubric } = useFetchQuestionRubricsApproveAnalyticsQuery({ token })
    const { questions, isFetching } = useRubricContext()

    // Flatten all questionRubrics from all questions
    const questionRubrics = questions?.flatMap(q => 
        (q.questionRubrics || []).map(qr => ({
            ...qr,
            questionName: q.name || q.text,
        }))
    ) || []

    const data =
        analyticsPerQuestionRubric && questionRubrics.length
            ? questionRubrics.map(questionRubric => ({
                  id: questionRubric.id,
                  question: questionRubric.name,
                  percent: analyticsPerQuestionRubric[questionRubric.id]?.reviewedByHuman || 0,
                  number: Math.round(
                      ((analyticsPerQuestionRubric[questionRubric.id]?.totalSubmissions || 0) *
                          (analyticsPerQuestionRubric[questionRubric.id]?.reviewedByHuman || 0)) /
                          100,
                  ),
              }))
            : []
    const handleBarClick = entry => {
        if (entry && entry.id) {
            navigate(buildAssignmentPath(token, entry.id))
        }
    }

    const chartHeight = Math.max(250, data.length * BAR_HEIGHT + 100)

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 60, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        domain={[0, 100]}
                        tickFormatter={value => `${value}%`}
                        label={{ value: 'Percent of Submissions', position: 'bottom', offset: 15 }}
                    />
                    <YAxis
                        dataKey='question'
                        type='category'
                        tick={{ fontSize: 12 }}
                        width={200}
                        tickFormatter={value => truncate(value, { length: 30 })}
                        label={{ value: 'Rubrics', angle: -90, position: 'left' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='percent' shape={<CustomBar />} onClick={data => handleBarClick(data)}>
                        <LabelList content={<CustomLabel />} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Spin>
    )
}

export default HumanReviewProgressChart
